import { useEffect, useRef, useState, useMemo } from "react";

import { AxiosResponse } from "axios";

import { Box, Grid, MenuItem, Skeleton } from "@mui/material";
import { CheckboxWithLabel, Select, TextField } from "formik-mui";
import { DatePicker } from "formik-mui-x-date-pickers";
import { Field, Form, Formik, FormikValues } from "formik";

import * as Yup from "yup";
import { parse } from "date-fns";
import { format } from "date-fns-tz";
import queryString from "query-string";
import { useSnackbar } from "notistack";

import { axios } from "../api";
import Sheet, { SheetContent, SheetHeader } from "../components/Sheet";
import LoadingSpinner from "../components/Loading";
import { ActionButton, CancelButton } from "../components/Buttons";
import CustomSnackbarContent from "../components/CustomSnackbarContent";

import { useConfig } from "../hooks/useConfig";
import PartActionHeader from "./PartActionHeader";
import { handleAPIErrors } from "../utils/FormikErrors";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useGlobalConfig } from "../hooks/useGlobalConfig";

import { Quote } from "../quotes/types";
import type { NestedPart, PriceOption } from "./types";
import { QuoteLineList } from "../quotes/types";
import { quoteLineSellPrice } from "../quotes/quoteBusinessLogic";
import { AeronetV6ItemsResponse } from "../types";
import { IAircraftSelect } from "../aircraft/types";

import { JobAutocomplete } from "../jobs/FormikJobAutocomplete";
import type { IJob } from "../jobs/types";
import FormikTaskAutoComplete from "../jobs/FormikTaskAutoComplete";
import { UserAutocomplete } from "../users/user-management/UserAutocomplete";
import { PartAutocomplete } from "./PartAutocomplete";
import { ItemPriceAutocomplete } from "./ItemPriceAutocomplete";
import { TypeAutocomplete } from "../types/TypeAutoComplete";
import { QuoteAutocomplete } from "../quotes/FormikQuoteAutocomplete";
import { ItemAutocomplete } from "../invoices/FormikItemAutocomplete";
import { AircraftAutocomplete } from "../sors/AircraftAutocomplete";

export interface RaiseDemandProps {
  open: boolean;
  jobId?: string | number;
  part?: NestedPart;
  raiseDemandType?: RaiseDemandTypeEnum;
  quoteLineList?: QuoteLineList;
  quote?: Quote;
  onClose: () => void;
  onSave?: () => void;
  isPartsOrders? : boolean;
  isTask?:boolean;
  handleOrderCreate?:any;
  /**
   * When true (default), a missing `jobId` for `FROM_INVENTORY` preselects Stock.
   * Set to false (e.g. inventory parts) to start with no work order (null/blank).
   */
  defaultWorkOrderToStock?: boolean;
}

export enum RaiseDemandTypeEnum {
  FROM_QUOTES = "FROM_QUOTES",
  FROM_JOBS = "FROM_JOBS",
  FROM_INVENTORY = "FROM_INVENTORY",
}

const STOCK_JOB_OPTION = { id: "0", name: "Stock" };

const getValidJobId = (value: unknown): string => {
  if (value === undefined || value === null) {
    return "";
  }
  const normalized = String(value).trim();
  if (!normalized) {
    return "";
  }
  return /^\d+$/.test(normalized) ? normalized : "";
};

const isCreateNewJobSelection = (value: unknown): boolean =>
  typeof value === "string" && value.startsWith("Create");

const RaiseDemand: React.FC<RaiseDemandProps> = (props) => {
  const {
    open,
    part,
    raiseDemandType,
    quoteLineList,
    quote,
    jobId,
    onClose,
    onSave,
    isTask=true,
    handleOrderCreate,
    defaultWorkOrderToStock = true,
  } = props;
  const formRef = useRef<FormikValues>();
  const handleAddDemand = () => {
    if ( Object.keys(formRef?.current?.errors).length === 1 && formRef?.current?.errors.job_id) {
      handleOrderCreate(formRef)
    }
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader onClose={onClose} title="Raise Demand">
        <PartActionHeader part={part} />
      </SheetHeader>
      <SheetContent>
        <RaiseDemandForm
          partId={part?.id ?? 0}
          jobId={jobId}
          quoteLineList={quoteLineList}
          formRef={formRef}
          part={part}
          raiseDemandType={raiseDemandType}
          quote={quote}
          onClose={onClose}
          onSave={onSave}
          isTask={isTask}
          onAddDemand={handleAddDemand}
          defaultWorkOrderToStock={defaultWorkOrderToStock}
        />
      </SheetContent>
    </Sheet>
  );
};

interface RaiseDemandFormProps {
  partId: number;
  part?: NestedPart;
  formRef: any;
  quote?: Quote;
  quoteLineList: QuoteLineList | undefined;
  raiseDemandType: RaiseDemandTypeEnum | undefined;
  jobId?: string | number;
  onClose: () => void | boolean;
  onSave?: () => void | boolean;
  isPartsOrders?:void | boolean;
  isTask?: boolean;
  onAddDemand?: () => void;
  defaultWorkOrderToStock?: boolean;
}

export const RaiseDemandForm = (props: RaiseDemandFormProps) => {
  const {
    formRef,
    partId,
    raiseDemandType,
    quoteLineList,
    quote,
    jobId,
    onClose,
    onSave,
    isTask,
    part,
    defaultWorkOrderToStock = true,
  } = props;
  const { enqueueSnackbar } = useSnackbar();
  const config = useConfig("jobs");
  const jobConfig = useConfig("jobs");
  const globalConfig = useGlobalConfig();
  const [aircraft, setAircraft] = useState<ReadonlyArray<IAircraftSelect>>([]);
  const createJobFormRef = useRef<{
    submitForm: () => Promise<IJob>;
  } | null>(null);
  const jobAutocompleteStatus = useMemo(() => [1, 2], []);
  const currentUser = useCurrentUser();
  /** Get a list of aircraft for the aircraft select. */
  useEffect(() => {
    let active = true;

    async function getAircraft() {
      const params = {
        filter: [
          JSON.stringify({ field: "active", value: true }),
          JSON.stringify({ field: "registration", op: "!=", value: null }),
          JSON.stringify({ field: "registration", op: "!=", value: "" }),
        ],
        sort: "registration",
        size: 100,
      };

      try {
        const resp: AxiosResponse<AeronetV6ItemsResponse<IAircraftSelect>> =
          await axios.get("/v6/aircraft", {
            params: params,
            paramsSerializer: (params) => queryString.stringify(params),
          });
        if (active) {
          setAircraft(resp.data.items);
        }
      } catch (e) {
        enqueueSnackbar("Error loading aircraft", {
          variant: "error",
          anchorOrigin: {
            vertical: "bottom",
            horizontal: "center",
          },
        });
        throw e;
      }
    }

    getAircraft();

    return () => {
      active = false;
    };
  }, [enqueueSnackbar]);

  const RaiseDemandSchema = useMemo(() => {
    return Yup.object().shape({
      part_id: Yup.string().notOneOf(["0"]).required("Required"),
      job_id: Yup.mixed().test("jobIdRequired", "Required", function (value) {
        const job = (this.parent as any)?.job;
        if (isCreateNewJobSelection(job) || isCreateNewJobSelection(value)) {
          return true;
        }
        if (value === "0" || value === 0) {
          return true;
        }
        if (getValidJobId(value)) {
          return true;
        }
        return false;
      }),
      job: Yup.mixed().test("jobOrId", "Required", function (value) {
        const job_id = (this.parent as any)?.job_id;
        if (job_id === "0" || job_id === 0) {
          return true;
        }
        if (getValidJobId(job_id)) {
          return true;
        }
        if (isCreateNewJobSelection(value)) {
          return true;
        }
        return (
          !!value &&
          typeof value === "object" &&
          !!getValidJobId((value as any)?.id)
        );
      }),
      quantity: Yup.number().when([], () => {
        return raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES
          ? Yup.number().positive().required("Required")
          : Yup.number().notRequired();
      }),
      priority: Yup.string().required("Required"),
    });
  }, [raiseDemandType]);

  const initialDemand = useMemo(() => {
    const defaultInventoryJobBlank =
      raiseDemandType === RaiseDemandTypeEnum.FROM_INVENTORY &&
      (jobId === undefined || jobId === null || jobId === "") &&
      defaultWorkOrderToStock === false;

    const normalizedJobId = defaultInventoryJobBlank
      ? null
      : jobId != null && jobId !== ""
        ? String(jobId)
        : "0";

    const jobInitial = defaultInventoryJobBlank
      ? null
      : normalizedJobId === "0"
        ? STOCK_JOB_OPTION
        : { id: normalizedJobId as string };

    return {
      part_id: partId,
      job: jobInitial,
      job_id: normalizedJobId,
      quantity:
        quoteLineList && quoteLineList.quantity ? quoteLineList.quantity : "",
      priority:
        config
          ? config.default_demand_priority
          : "",
      date_required:
        quoteLineList && quoteLineList.date_required
          ? parse(quoteLineList.date_required, "yyyy-MM-dd", new Date())
          : new Date(),
      status: "",
      raised_by: currentUser ?? "",
      date_raised: new Date(),
      note: quoteLineList?.note ?? "",
      task: null,
      task_id: null,
      uom: part?.uom || "",
      cost: quoteLineList && quoteLineList.price ? quoteLineList.price : null,
      lead_time: quoteLineList && quoteLineList.lead_time ? quoteLineList.lead_time : 0,
      lead_time_text: quoteLineList && quoteLineList.lead_time_text ? quoteLineList.lead_time_text : "",
      part: {
        id: 0,
      },
      module:raiseDemandType === RaiseDemandTypeEnum.FROM_QUOTES ? "quotes" : "",
      line_discount:quoteLineList && quoteLineList.line_discount ? quoteLineList.line_discount : 0,
      purpose: null,
      reference: "",
      item_id: null,
      quote_id: null,
      aircraft_id: null,
      item: null,
      quote: quote ? { id: quote.id } : null,
      aircraft: null,
      notify: false,
    };
    // Intentionally omit config/currentUser: including them reinitializes the form when
    // they load async and clears a user-selected task before submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [
    partId,
    jobId,
    quoteLineList,
    part?.uom,
    raiseDemandType,
    quote,
    defaultWorkOrderToStock,
  ]);

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialDemand}
      innerRef={formRef}
      validationSchema={RaiseDemandSchema}
      onSubmit={async (values, { setSubmitting, setErrors, setFieldValue }) => {
        const method = "post";
        const url = `/v6/quotes/raise_demand`;

        const jobBeforeSubmit = values.job;
        const taskBeforeSubmit = values.task;

        let effectiveJob: unknown = values.job;
        let effectiveJobId: string | number | null | undefined = values.job_id;
        let wasCreateNew = false;

        if (
          isCreateNewJobSelection(values.job) &&
          createJobFormRef.current
        ) {
          wasCreateNew = true;
          try {
            const newJob: IJob =
              await createJobFormRef.current.submitForm();
            setFieldValue("job", newJob as any);
            setFieldValue(
              "job_id",
              newJob.id != null ? String(newJob.id) : ""
            );
            effectiveJob = newJob;
            effectiveJobId =
              newJob.id != null ? String(newJob.id) : effectiveJobId;
          } catch {
            setSubmitting(false);
            enqueueSnackbar(
              "Fix the new work order fields above, then try again.",
              {
                variant: "error",
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "center",
                },
              }
            );
            return;
          }
        }

        const { task, item, quote, aircraft, purpose, job, part: partValue, ...rest } =
          values as any;

        const normalizeOptionalId = (value: any) => {
          if (value === "" || value === undefined || value === null) {
            return null;
          }
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : null;
        };

        const v = values as any;
        const resolvedTaskId = normalizeOptionalId(
          task?.id ?? v?.task_id ?? (typeof task === "number" || typeof task === "string" ? task : null)
        );
        const resolvedJobId =
          raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS
            ? normalizeOptionalId(v?.job_id ?? effectiveJobId)
            : effectiveJob &&
                typeof effectiveJob === "object" &&
                (effectiveJob as IJob).id != null &&
                (effectiveJob as IJob).id !== ""
              ? normalizeOptionalId((effectiveJob as IJob).id)
              : normalizeOptionalId(getValidJobId(effectiveJobId) || effectiveJobId);

        const data: Record<string, any> = {
          part_id:
            raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS
              ? normalizeOptionalId(v?.part?.id ?? partValue?.id ?? v?.part_id)
              : normalizeOptionalId(v?.part_id),
          job_id: resolvedJobId,
          quantity: rest.quantity,
          priority: rest.priority != null ? String(rest.priority) : rest.priority,
          date_required: format(values.date_required, "yyyy-MM-dd"),
          date_raised: format(values.date_raised, "yyyy-MM-dd"),
          status: rest.status != null ? String(rest.status) : rest.status,
          raised_by: normalizeOptionalId(v?.raised_by?.id ?? v?.raised_by),
          note: v?.note ?? "",
          task_id: resolvedTaskId,
          cost: rest.cost,
          lead_time: rest.lead_time,
          lead_time_text: rest.lead_time_text,
          module: rest.module ?? "",
          line_discount: rest.line_discount,
          function: rest.function,
          item_id: normalizeOptionalId(item?.id ?? v?.item_id),
          quote_id: normalizeOptionalId(quote?.id ?? v?.quote_id),
          aircraft_id: normalizeOptionalId(aircraft?.id ?? v?.aircraft_id),
          purpose: normalizeOptionalId(purpose?.id ?? purpose),
          reference: v?.reference ?? "",
          notify: !!v?.notify,
        };

        if (raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS) {
          let costNum = 0;
          const selectedOptionName = v?.value?.name as string | undefined;
          const selectedPart = v?.part || part;

          if (selectedOptionName && selectedOptionName.startsWith("List Price")) {
            const lp = (selectedPart as any)?.list_price ?? 0;
            costNum = Number(lp ?? 0);
          } else if (selectedOptionName && selectedOptionName.startsWith("At Cost")) {
            const lc = (selectedPart as any)?.list_cost ?? 0;
            costNum = Number(lc ?? 0);
          } else if (selectedOptionName && selectedOptionName.startsWith("Zero Value")) {
            costNum = 0;
          } else if (v?.value?.value != null) {
            costNum = Number(v.value.value);
          }

          data.cost = isNaN(costNum) ? 0 : costNum;
        } else {
          // TICKET-23424: price the SO line at the quote's saved sell, not list less a rounded discount.
          const sellPrice = quoteLineSellPrice(quoteLineList);
          if (sellPrice !== undefined) {
            data.sell_price = sellPrice;
          }
        }

        const apiCalls: Promise<AxiosResponse<any>>[] = [];

        apiCalls.push(
          axios({
            method: method,
            url: url,
            data: data,
          })
        );

        if (quote && quote.id && (data as any).job_id) {
          apiCalls.push(
            axios.patch(`/v6/quotes/${quote.id}`, { job_id: (data as any).job_id })
          );
        }

        try {
          await Promise.all(apiCalls);
          const jobIdForLink =
            (data as any).job_id != null && (data as any).job_id !== ""
              ? String((data as any).job_id)
              : "";
          let message = "Demand raised successfully";
          if (jobIdForLink) {
            message =
              raiseDemandType === RaiseDemandTypeEnum.FROM_QUOTES && wasCreateNew
                ? `Sales Order #${jobIdForLink} Created`
                : `Demand raised successfully #${jobIdForLink}`;
          }
          if (values.notify) {
            message = `${message}. Stores notified.`;
          }
          const uri = jobIdForLink ? `/salesorders/${jobIdForLink}` : undefined;
          enqueueSnackbar(
            <CustomSnackbarContent message={message} uri={uri} />,
            {
              variant: "success",
            }
          );
          onSave?.();
          onClose();
        } catch (error) {
          (values as any).job = jobBeforeSubmit;
          (values as any).task = taskBeforeSubmit;
          handleAPIErrors(error, setErrors);
          enqueueSnackbar("Error raising demand", {
            variant: "error",
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center",
            },
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, values, setFieldValue, handleBlur }) => {
        const basePart =
          raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS
            ? (values as any)?.part || part
            : part;

        let prices: PriceOption[] = [
          { name: "List Price", value: Number(0).toString() },
          { name: "At Cost", value: Number(0).toString() },
          { name: "Zero Value 0.00 USD", value: "0.00" },
        ];

        if (globalConfig && basePart) {
          const rounding = globalConfig?.value_rounding ?? 2;
          const listPrice = Number((basePart as any)?.list_price ?? 0);
          const listCost = Number((basePart as any)?.list_cost ?? 0);
          const listPriceCurrency = (basePart as any)?.list_price_currency ?? "";
          const listCostCurrency =
            (basePart as any)?.list_cost_currency ?? listPriceCurrency;

          prices = [
            {
              name: `List Price ${listPrice.toFixed(rounding)} ${listPriceCurrency}`,
              value: listPrice.toFixed(rounding).toString(),
            },
            {
              name: `At Cost ${listCost.toFixed(rounding)} ${listCostCurrency}`,
              value: listCost.toFixed(rounding).toString(),
            },
            {
              name: "Zero Value 0.00 USD",
              value: "0.00",
            },
          ];
        }

        return (
        <Form>
          <Field name="item_id" type="hidden" />
          <Field name="job_id" type="hidden" />
          {raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS && (
            <Field
              required
              id="part"
              name="part"
              component={PartAutocomplete}
            />
          )}
          {raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES ? (
            <>
              {globalConfig ? (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field
                      required
                      fullWidth
                      component={TextField}
                      name="quantity"
                      label="Quantity"
                      inputProps={{ inputMode: "decimal", min: 0 }}
                      onBlur={(event) => {
                        const value = Number(values.quantity);
                        const formatted = value.toFixed(
                          globalConfig.quantity_rounding
                        );
                        setFieldValue("quantity", formatted);
                        handleBlur(event);
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Field
                      fullWidth
                      component={TextField}
                      name="uom"
                      id="uom"
                      type="text"
                      label="Unit"
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
              )}
            </>
          ) : (
            <></>
          )}
          <Field
            component={UserAutocomplete}
            label="Raised By"
            name="raised_by"
            id="raised_by"
            companyId={""}
          />
          {globalConfig ? (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Field
                  required
                  fullWidth
                  component={DatePicker}
                  disablePast={true}
                  name="date_raised"
                  id="date_raised"
                  inputFormat={globalConfig?.date_format}
                  label="Date Raised"
                />
              </Grid>
              {raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES && (
                <Grid item xs={6}>
                  <Field
                    required
                    fullWidth
                    component={DatePicker}
                    disablePast={true}
                    name="date_required"
                    inputFormat={globalConfig?.date_format}
                    label="Date Required"
                  />
                </Grid>
              )}
            </Grid>
          ) : (
            <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
          )}
          
          {config ? (
            <Field
              required
              component={Select}
              id="priority"
              name="priority"
              label="Priority *"
            >
              {Object.entries(config.demand_priority_list).map(
                ([key, value]) => (
                  <MenuItem value={key} key={key}>
                    {value as unknown as string}
                  </MenuItem>
                )
              )}
            </Field>
          ) : (
            <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
          )}
          {[
            RaiseDemandTypeEnum.FROM_QUOTES,
            RaiseDemandTypeEnum.FROM_JOBS,
            RaiseDemandTypeEnum.FROM_INVENTORY,
          ].includes(raiseDemandType!) ? (
            <>
              {config ? (
                <Field
                  required
                  component={Select}
                  id="status"
                  name="status"
                  label="Status"
                >
                  {Object.entries(config.demand_status_list).map(
                    ([key, value]) => (
                      <MenuItem value={key} key={key}>
                        {value as unknown as string}
                      </MenuItem>
                    )
                  )}
                </Field>
              ) : (
                <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
              )}
              
              {raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES && (
                <>
                  {jobConfig?.demand_purpose_parent_id ? (
                    <Field
                      fullWidth
                      component={TypeAutocomplete}
                      name="purpose"
                      type="text"
                      label="Purpose"
                      parent_id={jobConfig?.demand_purpose_parent_id}
                    />
                  ) : (
                    <Skeleton animation="wave" sx={{ fontSize: "2rem" }} />
                  )}
                  <Field
                    fullWidth
                    component={TextField}
                    name="reference"
                    id="reference"
                    type="text"
                    label="Reference"
                  />
                  <Field name="aircraft_id" type="hidden" />
                  <Field
                    id="raise-demand-aircraft-autocomplete"
                    name="aircraft"
                    component={AircraftAutocomplete}
                    displayLable="Aircraft Registration"
                    onChange={() => {}}
                  />
                </>
              )}
              {raiseDemandType !== RaiseDemandTypeEnum.FROM_JOBS && (
                <Field
                  id="raise-demand-job-autocomplete"
                  name="job"
                  component={JobAutocomplete}
                  status={jobAutocompleteStatus}
                  quote={quote}
                  aircraft={aircraft}
                  label={"Work Order Number"}
                  showStockOption={
                    raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES
                  }
                  createTypeId2nd={
                    raiseDemandType === RaiseDemandTypeEnum.FROM_INVENTORY
                      ? "3"
                      : raiseDemandType === RaiseDemandTypeEnum.FROM_QUOTES
                      ? "2"
                      : undefined
                  }
                  showWorkOrderType={
                    raiseDemandType === RaiseDemandTypeEnum.FROM_INVENTORY
                  }
                  autoSubmitCreateJob={true}
                  deferJobCreationToParentSubmit={true}
                  nestedFormRef={createJobFormRef}
                />
              )}
              {isTask && (
                <>
                  <Field name="task_id" type="hidden" />
                  <Field
                    id="task"
                    name="task"
                    component={FormikTaskAutoComplete}
                    label="Task"
                  />
                </>
              )}
              {raiseDemandType !== RaiseDemandTypeEnum.FROM_QUOTES && (
                <>
                  <Field
                    id="raise-demand-item-autocomplete"
                    name="item"
                    component={ItemAutocomplete}
                    pardId={Number(values?.part_id || 0)}
                    filterOptions={(x: any) => x}
                    onOpen={() => {
                      setFieldValue("item", null);
                    }}
                    onChange={() => {}}
                  />
                  <Field name="quote_id" type="hidden" />
                  <Field
                    id="raise-demand-quote-autocomplete"
                    name="quote"
                    component={QuoteAutocomplete}
                    disableCreateNew={true}
                  />
                </>
              )}
            </>
          ) : null}
          {raiseDemandType === RaiseDemandTypeEnum.FROM_JOBS ? (
            <Field
              required
              id="value"
              name="value"
              freeSolo
              component={ItemPriceAutocomplete as any}
              label="Value"
              prices={prices}
              disabled={prices.length === 0}
              perUnitSuffix={true}
            />
          ) : null}
          <Field
            fullWidth
            multiline
            rows={3}
            component={TextField}
            name="note"
            id="note"
            type="text"
            label="Notes"
          />
          <Field
            component={CheckboxWithLabel}
            name="notify"
            type="checkbox"
            color="primary"
            Label={{
              label: "Notify Stores",
              sx: { px: 1, pb: 0.5 },
            }}
          />
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <CancelButton onClick={onClose as any} />
            <ActionButton children={"Submit"} onClick={props.onAddDemand as any} />
          </Box>
          <br />
          {isSubmitting && <LoadingSpinner />}
        </Form>
        );
      }}
    </Formik>
  );
};

export default RaiseDemand;
